import { useState } from "react";
import { useEmbedOptions } from "../../../features/embed/embedOptionsState";
import { Icon } from "../../kit";
import { CompactCheck, CompactNumber, MiniBtn } from "../inspector/rows";
import { useStudioModelId } from "../studioModel";

/**
 * The reference's Embed panel.
 *
 * Every field it shows already existed in `EmbedOptions` and is persisted
 * through the Vault API — this surfaces them where the reference puts them
 * rather than only inside a modal two screens away. Both surfaces share
 * `useEmbedOptions`, so they cannot disagree about what is stored.
 *
 * NOT cloned: the reference's three mode tabs. The capture recorded that there
 * are three and nothing about what they are, and three invented labels would be
 * worse than none. "High Resolution Cover" is likewise absent — there is no
 * field for it, and adding one is a Vault-API change, not a layout one.
 */
/** `title` / `pad`: the Basic Share panel shows this with its own words and
 * its 18px panel gutter; the Advanced left rail uses the defaults. */
export function EmbedPanel({ title = "Embed", pad = "p-3" }: { title?: string; pad?: string } = {}) {
  const modelId = useStudioModelId();
  const { embed, opts, set, loading, snippet, embedUrl } = useEmbedOptions(modelId, { autosave: true });
  const [copied, setCopied] = useState(false);
  const [showCode, setShowCode] = useState(false);

  if (!modelId) {
    return (
      <div className={`flex flex-col gap-2 ${pad}`}>
        <span className="text-[12px] font-semibold text-ed-text-2">{title}</span>
        <p className="text-[12px] leading-snug text-ed-text-3">
          Your website code comes from the Vault: upload this file there and open it
          in the studio from the Vault — the code to copy into any page appears here.
        </p>
        {/* A real action, not just a sentence. A new tab, so nothing open here is lost. */}
        <a
          href="/vault"
          target="_blank"
          rel="noopener"
          className="inline-flex h-8 w-fit items-center gap-1.5 rounded-lg border border-ed-line px-3 text-[12px] font-medium text-ed-text hover:bg-ed-hover"
          data-open-vault
        >
          Open your Vault
          <span aria-hidden>↗</span>
        </a>
      </div>
    );
  }

  const copy = () => {
    if (!snippet) return;
    void navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  return (
    <div className={`flex flex-col gap-3 ${pad}`}>
      <span className="text-[12px] font-semibold text-ed-text-2">{title}</span>

      {loading ? (
        <span className="flex items-center gap-1.5 text-[12px] text-ed-text-3">
          <Icon name="progress_activity" size={13} className="animate-spin" /> Loading…
        </span>
      ) : !embed ? (
        <span className="text-[11px] leading-snug text-ed-text-red">
          Could not load this model’s embed.
        </span>
      ) : (
        <>
          <MiniBtn icon={copied ? "check" : "content_copy"} primary onClick={copy}>
            {copied ? "Copied" : "Copy HTML code"}
          </MiniBtn>
          <div className="flex gap-1.5">
            <MiniBtn icon="code" onClick={() => setShowCode((v) => !v)}>
              {showCode ? "Hide code" : "View code"}
            </MiniBtn>
            <MiniBtn icon="open_in_new" onClick={() => window.open(embedUrl, "_blank", "noopener")}>
              Preview
            </MiniBtn>
          </div>
          {showCode ? (
            <textarea
              readOnly
              value={snippet}
              rows={4}
              className="scroll-thin w-full resize-none rounded border border-ed-border bg-white p-2 font-mono text-[11px] leading-snug text-ed-text"
            />
          ) : null}

          <div className="flex flex-col gap-2 border-t border-ed-border pt-2">
            <label className="flex items-center justify-between gap-2">
              <span className="text-[12px] text-ed-text-2">Responsive</span>
              <CompactCheck checked={opts.responsive !== false} onChange={(v) => set({ responsive: v })} />
            </label>
            {opts.responsive === false ? (
              <div className="flex items-center gap-1.5">
                <CompactNumber value={opts.width ?? 1280} min={1} step={1} width={70} onChange={(v) => set({ width: v })} />
                <span className="text-[12px] text-ed-text-3">×</span>
                <CompactNumber value={opts.height ?? 720} min={1} step={1} width={70} onChange={(v) => set({ height: v })} />
              </div>
            ) : null}
            <label className="flex items-center justify-between gap-2">
              <span className="text-[12px] text-ed-text-2">Title</span>
              <CompactCheck checked={opts.showTitle !== false} onChange={(v) => set({ showTitle: v })} />
            </label>
            <label className="flex items-center justify-between gap-2">
              <span className="text-[12px] text-ed-text-2">Remove backlink</span>
              <CompactCheck checked={!!opts.removeBacklink} onChange={(v) => set({ removeBacklink: v })} />
            </label>
            <label className="flex items-center justify-between gap-2">
              <span className="text-[12px] text-ed-text-2">Allow download</span>
              <CompactCheck checked={!!opts.allowDownload} onChange={(v) => set({ allowDownload: v })} />
            </label>
          </div>

          <p className="flex items-start gap-1.5 text-[11px] leading-snug text-ed-text-3">
            <Icon name="info" size={13} className="mt-px shrink-0" />
            Changes save automatically and apply to the live embed.
          </p>
        </>
      )}
    </div>
  );
}
